import { useRef, useState } from 'react';
import { ThemeButton } from './ThemeWrapper';
import { dataStagingAPI } from '../services/api';

/**
 * Upload CSV/XLSX into document staging (evaluate, no auto-apply).
 */
export default function StagingFileImport({ onImported }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState('');

  const onFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    setFile(next);
    setError('');
    setSummary('');
  };

  const upload = async () => {
    if (!file) {
      setError('Choose a CSV or Excel (.xlsx) file first');
      return;
    }
    setUploading(true);
    setError('');
    setSummary('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('applyAuto', 'true');
      const res = await dataStagingAPI.ingestUpload(formData);
      const data = res.data || {};
      setSummary(
        `Imported ${data.rowCount ?? data.documentCount ?? 0} row(s)` +
          (data.appliedCount != null ? ` · ${data.appliedCount} saved to SQL` : '') +
          (data.skippedNoEmail ? ` · ${data.skippedNoEmail} skipped (no name/email)` : '') +
          (data.conflictCount != null ? ` · ${data.conflictCount} need review` : '') +
          (data.batch?.id ? ` · batch ${data.batch.id}` : '')
      );
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      if (typeof onImported === 'function') await onImported(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="theme-card rounded-lg p-4 mb-4">
      <h2 className="font-semibold theme-text mb-1">Import into staging</h2>
      <p className="text-sm theme-text-secondary mb-3">
        Upload a CSV or Excel (.xlsx) sheet. Named students are saved to SQL even if some fields
        are missing; incomplete fields and duplicate emails stay in the review queue below.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={onFileChange}
          className="text-sm theme-text"
        />
        <ThemeButton type="button" onClick={upload} disabled={uploading || !file}>
          {uploading ? 'Importing…' : 'Import file'}
        </ThemeButton>
      </div>
      {file && (
        <p className="text-xs theme-text-secondary mt-2">Selected: {file.name}</p>
      )}
      {error && <p className="text-sm theme-error-text mt-2">{error}</p>}
      {summary && <p className="text-sm theme-text mt-2">{summary}</p>}
    </div>
  );
}
